import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiClient, call } from "./client";
import { isApiProblem } from "./problem";
import type { User } from "./types";
import { userLabel } from "../lib/personLabel";

/** Queries shared by several screens. Feature-specific queries live with their feature. */

export const queryKeys = {
  projects: ["projects"] as const,
  project: (key: string) => ["projects", key] as const,
  users: ["users", "directory"] as const,
  needsAttention: ["me", "needs-attention"] as const,
  item: (ref: string) => ["item", ref] as const,
  itemPart: (ref: string, part: string) => ["item", ref, part] as const,
  items: ["items"] as const,
  projectMembers: (key: string) => ["projects", key, "members"] as const,
};

export function useProjects() {
  return useQuery({ queryKey: queryKeys.projects, queryFn: () => call(apiClient.GET("/projects")), staleTime: 5 * 60_000 });
}

/**
 * One project by key (getProject). The server answers 404 for a project the caller holds no role on, identical to a
 * missing one (project visibility ruling, 1 Oct 2026), so a direct URL to it renders "not found". A 404 is final: no retry.
 */
export function useProject(projectKey: string | undefined) {
  return useQuery({
    queryKey: queryKeys.project(projectKey ?? ""),
    queryFn: () => call(apiClient.GET("/projects/{projectKey}", { params: { path: { projectKey: projectKey ?? "" } } })),
    enabled: Boolean(projectKey),
    staleTime: 5 * 60_000,
    retry: (failures, error) => !(isApiProblem(error) && error.status === 404) && failures < 2,
  });
}

const MAX_USER_PAGES = 10;

async function fetchUserDirectory(): Promise<User[]> {
  const all: User[] = [];
  let cursor: string | undefined;
  for (let pageNo = 0; pageNo < MAX_USER_PAGES; pageNo++) {
    const page = await call(apiClient.GET("/users", { params: { query: { limit: 200, cursor } } }));
    all.push(...page.items);
    // Stop on the last page, and never loop on a cursor the server hands back unchanged.
    if (!page.nextCursor || page.nextCursor === cursor) break;
    cursor = page.nextCursor;
  }
  return all;
}

/** Everyone the caller may see (partners: members of their visible items only). */
export function useUserDirectory() {
  const query = useQuery({ queryKey: queryKeys.users, queryFn: fetchUserDirectory, staleTime: 5 * 60_000 });
  const byId = useMemo(() => new Map((query.data ?? []).map((u) => [u.id, u])), [query.data]);
  return { ...query, users: query.data ?? [], byId };
}

/**
 * A project's ACTIVE members (`listProjectMembers`, contract 0.10.61, batch 13): the people an assignee is picked from and the
 * board's people filter shows. `memberIds` is undefined until known, and stays undefined when the server will not say (404
 * for a partner caller, who never sees membership): callers then keep their earlier behaviour (the user directory) rather
 * than offering nobody.
 */
export function useProjectMembers(projectKey: string | undefined) {
  const query = useQuery({
    queryKey: queryKeys.projectMembers(projectKey ?? ""),
    queryFn: () => call(apiClient.GET("/projects/{projectKey}/members", { params: { path: { projectKey: projectKey ?? "" } } })),
    enabled: Boolean(projectKey),
    staleTime: 5 * 60_000,
    // Membership only narrows a picker; without it the picker falls back to the directory. So never retry, never block.
    retry: false,
  });
  const memberIds = useMemo(() => (query.data ? new Set(query.data.map((member) => member.userId)) : undefined), [query.data]);
  return { ...query, memberIds };
}

/**
 * How a person is shown (Revision 94): "Display name (Real name)" when they set a display name, else the real name. The
 * name is `label` from the server, so every screen that resolves a person through the directory shows the same text.
 */
export function displayName(byId: Map<number, User>, id: number | null | undefined): string {
  if (id === null || id === undefined) return "Unassigned";
  const user = byId.get(id);
  return user ? userLabel(user) : "Unknown user";
}

/** The person's picture URL from the directory, or null (initials). */
export function avatarUrlOf(byId: Map<number, User>, id: number | null | undefined): string | null {
  if (id === null || id === undefined) return null;
  return byId.get(id)?.avatarUrl ?? null;
}

/** Crew roles (Revision 104): whether the person is an AI agent's account (the agent ring on their avatar). */
export function isAgentOf(byId: Map<number, User>, id: number | null | undefined): boolean {
  if (id === null || id === undefined) return false;
  return byId.get(id)?.isAgent === true;
}

/** Thai script. */
const THAI_CHAR = /[\u0E00-\u0E7F]/u;
/** Thai consonants ก..ฮ (U+0E01–U+0E2E): the only Thai letters that stand as an initial. */
const THAI_CONSONANT = /[\u0E01-\u0E2E]/u;
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;
const COMBINING = /\p{M}/u;

/**
 * The letters of a word that can stand as an initial. A Thai word gives only its consonants, so leading vowels (เ แ โ ใ ไ),
 * following vowels (า ำ) and combining marks (tone marks, vowel signs) are skipped; any other word gives its letters and digits.
 */
function initialChars(word: string): string[] {
  const chars = [...word].filter((char) => LETTER_OR_DIGIT.test(char) && !COMBINING.test(char));
  return chars.some((char) => THAI_CHAR.test(char)) ? chars.filter((char) => THAI_CONSONANT.test(char)) : chars;
}

/**
 * A person's initials for an avatar circle (rule of 8 Oct 2026, D-S2-02; replaces R-AV-2/R-AV-3):
 * - the first-name initial and the SURNAME initial: the first and the last word ("Tanwa Pasawano" is "TP"; a middle name is
 *   skipped). A trailing nickname in brackets is not a surname ("Tanwa Pasawano (Ta)" is still "TP");
 * - Thai: the first consonant of the first name and of the surname, leading vowels and marks skipped ("เกียรติ ใจดี" is "กจ");
 * - only when there is no surname, the first two letters of the one name ("Tanwa" is "Ta", Thai "แก้ว" is "กว").
 */
export function initials(name: string): string {
  const withoutNick = name.replace(/\([^)]*\)/gu, " ");
  const source = withoutNick.trim() ? withoutNick : name;
  const words = source.split(/\s+/u).map(initialChars).filter((chars) => chars.length > 0);
  const first = words[0];
  if (!first) return "";
  const surname = words.length > 1 ? words[words.length - 1] : undefined;
  if (surname) return `${first[0] ?? ""}${surname[0] ?? ""}`.toUpperCase();
  return `${(first[0] ?? "").toUpperCase()}${(first[1] ?? "").toLowerCase()}`;
}

/** Batch 17 attention-bell merge §3.3: the header bell's cadence, so its number never goes stale (AC-M6). */
export const NEEDS_ATTENTION_POLL_MS = 60_000;

/**
 * GET /me/needs-attention. One query key for the header bell, its panel and Home, so the three always show the same number.
 * `poll` (the header bell): re-read every 60 s while the page is visible (react-query pauses the interval while the document is
 * hidden) and always on returning to the browser tab; the other observers share whatever it fetched.
 */
export function useNeedsAttention(enabled: boolean, { poll = false }: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.needsAttention,
    queryFn: () => call(apiClient.GET("/me/needs-attention")),
    enabled,
    staleTime: poll ? 30_000 : 60_000,
    ...(poll ? { refetchInterval: NEEDS_ATTENTION_POLL_MS, refetchIntervalInBackground: false, refetchOnWindowFocus: "always" as const } : {}),
  });
}
